(() => {
  'use strict';
  const PHYSICS = Object.freeze({ gravity: 1120, impulse: 500, buffer: 0.12, step: 1 / 120 });
  const PROGRESSION_DISTANCE = 14000 / 3;
  const CRASH_DURATION = 0.65;
  const HAZARDS = Object.freeze({
    cone: Object.freeze({ width: 30, height: 35 }),
    tires: Object.freeze({ width: 42, height: 64 }),
    barrier: Object.freeze({ width: 68, height: 49 }),
    roller: Object.freeze({ width: 44, height: 44 })
  });
  class PexRaceModel {
    constructor(random = Math.random) { this.random = random; this.width = 760; this.reset(); }
    get playerX() { return Math.min(150, Math.max(64, this.width * 0.22)); }
    // Difficulty follows distance, so taking a coin never makes the next cone harder.
    get progress() { return Math.min(1, this.distance / PROGRESSION_DISTANCE); }
    get speed() { return 250 + 190 * this.progress; }
    get gap() { return 560 - 100 * this.progress; }
    reset() {
      this.state = 'ready'; this.y = 0; this.vy = 0; this.score = 0; this.coins = 0;
      this.elapsed = 0; this.distance = 0; this.obstacles = []; this.pickups = []; this.nextSpawn = 0.7;
      this.buffer = 0; this.jumps = 0; this.serial = 0; this.lastSpawnDistance = -Infinity;
      this.shield = false; this.nextShieldAt = 100; this.slowBag = []; this.slow = 0; this.timeScale = 1; this.takeoff = 0; this.landing = 0;
      this.events = [];
      this.hazardBag = [];
    }
    start() { this.reset(); this.state = 'running'; }
    pause() { if (this.state === 'running') { this.state = 'paused'; this.buffer = 0; } }
    resume() { if (this.state === 'paused') this.state = 'running'; }
    resize(width) {
      const oldX = this.playerX;
      this.width = Math.max(1, width);
      const shift = this.playerX - oldX;
      [...this.obstacles, ...this.pickups].forEach(item => { item.x += shift; });
    }
    jump() {
      if (this.state !== 'running') return false;
      if (this.y === 0) {
        this.vy = PHYSICS.impulse; this.buffer = 0; this.jumps++; this.takeoff = 0.18; this.landing = 0; return true;
      }
      if (this.vy < 0 && this.y < 48) this.buffer = PHYSICS.buffer;
      return false;
    }
    nextGap() { return Math.max(460, this.gap - 60) + this.random() * (220 - 40 * this.progress); }
    nextHazard(id) {
      if (id <= 2) return 'cone';
      if (id <= 5) return ['tires', 'barrier', 'roller'][id - 3];
      if (!this.hazardBag.length) {
        this.hazardBag = ['cone', 'tires', 'barrier', 'roller'];
        for (let i = this.hazardBag.length - 1; i > 0; i--) {
          const j = Math.floor(this.random() * (i + 1));
          [this.hazardBag[i], this.hazardBag[j]] = [this.hazardBag[j], this.hazardBag[i]];
        }
      }
      return this.hazardBag.pop();
    }
    slowOpportunity() {
      // Baseline: one opportunity per eight cones. Exactly three of each five
      // opportunities survive, shuffled: 3/40 instead of 1/8 (40% fewer).
      if (!this.slowBag.length) {
        this.slowBag = [true, true, true, false, false];
        for (let i = this.slowBag.length - 1; i > 0; i--) {
          const j = Math.floor(this.random() * (i + 1));
          [this.slowBag[i], this.slowBag[j]] = [this.slowBag[j], this.slowBag[i]];
        }
      }
      return this.slowBag.pop();
    }
    spawn() {
      const x = this.width + 32, id = ++this.serial, speed = this.speed;
      const type = this.nextHazard(id);
      this.obstacles.push({ id, type, x, ...HAZARDS[type], age: 0, offset: 0, passed: false });
      // Three coins trace an achievable jump. Low coins sit well between cones.
      for (const [offset, height] of [[-0.17, 123], [0.03, 153], [0.23, 137]]) {
        this.pickups.push({ type: 'coin', x: x + speed * offset, height, collected: false });
      }
      if (this.gap / speed > 1.5) this.pickups.push({ type: 'coin', x: x + speed * 0.78, height: 34, collected: false });
      if (id % 8 === 6 && this.slowOpportunity()) this.pickups.push({ type: 'slow', x: x + speed * 0.68, height: 45, collected: false });
      this.lastSpawnDistance = this.distance;
      this.nextSpawn += this.nextGap() / speed;
    }
    collect(pickup) {
      if (pickup.collected) return;
      pickup.collected = true;
      if (pickup.type === 'coin') {
        this.coins++;
        if (this.coins >= this.nextShieldAt) {
          this.shield = true; this.nextShieldAt += 100; this.events.push('shield');
        }
      } else if (pickup.type === 'slow') { this.slow = 6; this.events.push('slow'); }
    }
    tick(dt) {
      if (this.state !== 'running' || !Number.isFinite(dt) || dt <= 0) return;
      let remaining = Math.min(dt, 0.1);
      while (remaining > 0.000001 && this.state === 'running') {
        const realStep = Math.min(PHYSICS.step, remaining); remaining -= realStep;
        this.elapsed += realStep; this.slow = Math.max(0, this.slow - realStep);
        // Slow the entire simulation, including the jump. Ease out before expiry;
        // the path relative to a cone stays identical throughout the transition.
        const targetScale = this.slow > 0 ? 0.72 + 0.28 * (1 - Math.min(1, this.slow / 1.2)) : 1;
        const change = Math.max(-realStep * 0.7, Math.min(realStep * 0.7, targetScale - this.timeScale));
        this.timeScale += change;
        const step = realStep * this.timeScale;
        this.buffer = Math.max(0, this.buffer - step);
        this.takeoff = Math.max(0, this.takeoff - step); this.landing = Math.max(0, this.landing - step);
        if (this.y > 0 || this.vy > 0) {
          this.y += this.vy * step - PHYSICS.gravity * step * step / 2;
          this.vy -= PHYSICS.gravity * step;
          if (this.y <= 0) {
            this.y = 0; this.vy = 0; this.landing = 0.24;
            if (this.buffer > 0) this.jump();
          }
        }
        const movement = this.speed * step;
        this.distance += movement; this.nextSpawn -= step;
        if (this.nextSpawn <= 0 && this.distance - this.lastSpawnDistance >= 460) this.spawn();
        for (const pickup of this.pickups) {
          pickup.x -= movement;
          if (!pickup.collected && Math.abs(pickup.x - this.playerX) < 25 &&
            (pickup.type !== 'coin' || Math.abs(pickup.height - (this.y + 42)) < 24)) this.collect(pickup);
        }
        for (const obstacle of this.obstacles) {
          obstacle.x -= movement;
          if (obstacle.type === 'roller') {
            obstacle.age += step;
            const offset = Math.sin(obstacle.age * 4) * 14;
            obstacle.x += offset - obstacle.offset;
            obstacle.offset = offset;
          }
          const overlaps = obstacle.x + obstacle.width - 5 > this.playerX - 27 && obstacle.x + 5 < this.playerX + 28;
          if (!obstacle.passed && overlaps && this.y < obstacle.height - 7) {
            if (this.shield) {
              this.shield = false; obstacle.passed = true; obstacle.broken = true; this.score++; this.events.push('saved');
            } else { this.state = 'finished'; this.buffer = 0; this.shield = false; this.slow = 0; this.timeScale = 1; break; }
          }
          if (!obstacle.passed && obstacle.x + obstacle.width < this.playerX - 27) { obstacle.passed = true; this.score++; }
        }
        this.obstacles = this.obstacles.filter(obstacle => obstacle.x > -70 && !obstacle.broken);
        this.pickups = this.pickups.filter(pickup => pickup.x > -30 && !pickup.collected);
      }
    }
  }

  class PexCrashSound {
    constructor() { this.muted = false; this.context = null; this.voices = new Set(); this.musicVoices = new Set(); this.musicBus = null; this.musicStep = 0; this.nextNote = 0; }
    unlock() {
      if (this.muted) return;
      try {
        const Audio = window.AudioContext || window.webkitAudioContext;
        if (!Audio) return;
        if (!this.context || this.context.state === 'closed') this.context = new Audio();
        if (this.context.state === 'suspended') this.context.resume().catch(() => {});
      } catch (_) { this.context = null; }
    }
    play() {
      const ctx = this.context;
      if (this.muted || !ctx || ctx.state !== 'running') return;
      try {
        const t = ctx.currentTime, length = Math.ceil(ctx.sampleRate * 0.14);
        const buffer = ctx.createBuffer(1, length, ctx.sampleRate), data = buffer.getChannelData(0);
        for (let i = 0; i < length; i++) {
          const u = i / ctx.sampleRate;
          data[i] = (Math.random() * 2 - 1) * Math.exp(-u * 36) * 0.7 + Math.sin(2 * Math.PI * (155 * u - 260 * u * u)) * Math.exp(-u * 45) * 0.3;
        }
        const source = ctx.createBufferSource(), filter = ctx.createBiquadFilter(), gain = ctx.createGain();
        source.buffer = buffer; filter.type = 'lowpass'; filter.frequency.value = 1800;
        gain.gain.setValueAtTime(0.22, t); gain.gain.exponentialRampToValueAtTime(0.001, t + 0.14);
        source.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
        this.voices.add(source);
        source.onended = () => { this.voices.delete(source); source.disconnect(); filter.disconnect(); gain.disconnect(); };
        source.start(t); source.stop(t + 0.14);
      } catch (_) { /* Unsupported audio must never interrupt gameplay. */ }
    }
    music() {
      const ctx = this.context;
      if (this.muted || !ctx || ctx.state !== 'running') return;
      try {
        if (!this.musicBus) {
          this.musicBus = ctx.createGain(); this.musicBus.gain.value = 0.24; this.musicBus.connect(ctx.destination);
          this.musicStep = 0; this.nextNote = ctx.currentTime + 0.02;
        }
        const stepLength = 60 / 136 / 2;
        const tune = [0, 7, 12, null, 7, 3, 10, 7, 12, 7, 3, null, 10, 7, 3, 7];
        while (this.nextNote < ctx.currentTime + 0.12) {
          const step = this.musicStep, root = [45, 41, 38, 43][Math.floor(step / 8) % 4];
          const hz = note => 440 * Math.pow(2, (note - 69) / 12);
          this.tone(hz(root + (step % 4 === 3 ? 12 : 0)), this.nextNote, stepLength * 0.68, 0.14, 'triangle');
          const note = tune[step % tune.length];
          if (note !== null) this.tone(hz(root + 24 + note), this.nextNote, stepLength * 0.55, 0.045, 'square');
          if (step % 2 === 0) this.tone(72, this.nextNote, 0.09, 0.18, 'sine');
          this.musicStep = (step + 1) % 64; this.nextNote += stepLength;
        }
      } catch (_) { this.stopMusic(); }
    }
    tone(frequency, time, duration, volume, type) {
      const ctx = this.context, voice = ctx.createOscillator(), filter = ctx.createBiquadFilter(), gain = ctx.createGain();
      voice.type = type; voice.frequency.setValueAtTime(frequency, time);
      filter.type = 'lowpass'; filter.frequency.value = type === 'square' ? 1450 : 900;
      gain.gain.setValueAtTime(0.001, time); gain.gain.linearRampToValueAtTime(volume, time + 0.008);
      gain.gain.exponentialRampToValueAtTime(0.001, time + duration);
      voice.connect(filter); filter.connect(gain); gain.connect(this.musicBus);
      this.musicVoices.add(voice);
      voice.onended = () => { this.musicVoices.delete(voice); voice.disconnect(); filter.disconnect(); gain.disconnect(); };
      voice.start(time); voice.stop(time + duration + 0.01);
    }
    stopMusic() {
      for (const voice of this.musicVoices) { try { voice.stop(); voice.disconnect(); } catch (_) {} }
      this.musicVoices.clear();
      if (this.musicBus) { this.musicBus.disconnect(); this.musicBus = null; }
    }
    silence() { this.stopMusic(); for (const voice of this.voices) { try { voice.stop(); } catch (_) {} } this.voices.clear(); }
    toggle() { this.muted = !this.muted; if (this.muted) this.silence(); else this.unlock(); }
    close() {
      this.silence(); const context = this.context; this.context = null;
      if (context && context.state !== 'closed') context.close().catch(() => {});
    }
  }

  class PexRace {
    constructor(root) {
      this.root = root; this.model = new PexRaceModel(); this.abort = new AbortController();
      this.signal = this.abort.signal; this.frame = 0; this.last = null; this.visible = false;
      this.sound = new PexCrashSound(); this.crashing = false; this.crashAge = CRASH_DURATION;
      this.best = 0; this.finishAt = -Infinity; this.held = new Set(); this.destroyed = false;
      this.text = JSON.parse(root.querySelector('[data-pps-copy]').textContent);
      const find = name => root.querySelector(`[data-pps-${name}]`);
      this.details = root.querySelector('details'); this.stage = find('stage'); this.canvas = find('canvas');
      this.ctx = this.canvas.getContext('2d'); if (!this.ctx) return;
      this.action = find('action'); this.pauseButton = find('pause'); this.closeButton = find('exit');
      this.overlay = find('overlay'); this.title = find('title'); this.description = find('description');
      this.score = find('score'); this.bestNode = find('best'); this.live = find('live');
      this.soundButton = find('sound'); this.shieldNode = find('shield'); this.slowNode = find('slow');
      this.media = matchMedia('(prefers-reduced-motion: reduce)'); this.reduced = this.media.matches;
      this.pex = root.querySelector('[data-pps-art]');
      this.pex.addEventListener('load', () => this.render(), { signal: this.signal });
      find('app').hidden = false; find('fallback').hidden = true;
      this.action.addEventListener('pointerdown', event => {
        if (event.isPrimary === false || event.button > 0) return;
        event.preventDefault(); this.action.focus({ preventScroll: true }); this.act();
      }, { signal: this.signal });
      this.action.addEventListener('click', event => { if (event.detail === 0) this.act(); }, { signal: this.signal });
      this.stage.addEventListener('pointerdown', event => {
        if (event.isPrimary === false || event.button > 0) return;
        event.preventDefault(); this.stage.focus({ preventScroll: true }); this.act();
      }, { signal: this.signal });
      this.soundButton.addEventListener('click', () => { this.sound.toggle(); this.syncSound(); }, { signal: this.signal });
      this.pauseButton.addEventListener('click', () => this.pause(), { signal: this.signal });
      this.closeButton.addEventListener('click', () => this.close(), { signal: this.signal });
      root.addEventListener('keydown', event => this.key(event), { signal: this.signal });
      root.addEventListener('keyup', event => {
        this.held.delete(event.code || event.key);
        if ((event.target === this.action || event.target === this.stage) && [' ', 'Enter', 'ArrowUp'].includes(event.key)) event.preventDefault();
      }, { signal: this.signal });
      root.addEventListener('focusout', event => {
        if (!root.contains(event.relatedTarget)) { this.held.clear(); this.pause(); }
      }, { signal: this.signal });
      this.details.addEventListener('toggle', () => {
        if (!this.details.open) this.pause();
        else { this.loadArt(); this.resize(); }
      }, { signal: this.signal });
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.pause(this.text.away); }, { signal: this.signal });
      window.addEventListener('blur', () => { this.held.clear(); this.pause(this.text.away); }, { signal: this.signal });
      this.media.addEventListener('change', () => {
        this.reduced = this.media.matches; this.pause(this.text.motion); this.render();
      }, { signal: this.signal });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].intersectionRatio >= 0.55;
        if (!this.visible) this.pause(this.text.away);
      }, { threshold: [0, 0.55] });
      this.observer.observe(this.stage);
      this.resizeObserver = new ResizeObserver(() => this.resize()); this.resizeObserver.observe(this.stage);
      this.sync(); this.resize();
    }
    loadArt() { if (!this.pex.getAttribute('src')) this.pex.src = this.pex.dataset.src; }
    resize() {
      const rect = this.stage.getBoundingClientRect(); if (!rect.width || !rect.height) return;
      const width = rect.width / rect.height * 330;
      if (Math.abs(width - this.model.width) > 3) this.pause();
      this.model.resize(width);
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      this.canvas.width = Math.round(rect.width * dpr); this.canvas.height = Math.round(rect.height * dpr);
      this.render();
    }
    key(event) {
      if (!this.details.open) return;
      if (event.key === 'Escape') { event.preventDefault(); if (!event.repeat) this.close(); return; }
      if (event.target !== this.stage && event.target !== this.action) return;
      if (event.key.toLowerCase() === 'p') { event.preventDefault(); if (!event.repeat) this.pause(); return; }
      if (![' ', 'Enter', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      const code = event.code || event.key;
      if (event.repeat || this.held.has(code)) return;
      this.held.add(code); this.act();
    }
    act() {
      if (document.hidden || !this.details.open || !this.visible || this.destroyed) return;
      const state = this.model.state;
      if (state === 'finished' && (this.crashing || performance.now() - this.finishAt < 350)) return;
      this.sound.unlock();
      this.loadArt();
      if (state === 'ready' || state === 'finished') { this.crashing = false; this.crashAge = CRASH_DURATION; this.model.start(); this.announce(this.text.started); }
      else if (state === 'paused') { this.model.resume(); this.announce(this.text.resumed); }
      else this.model.jump();
      this.sync(); this.run();
    }
    announce(text) { this.live.textContent = text; }
    sync() {
      const state = this.model.state;
      this.root.dataset.ppsState = state;
      this.updateHud();
      this.bestNode.textContent = String(this.best).padStart(3, '0');
      this.overlay.hidden = state === 'running' || this.crashing; this.pauseButton.hidden = state !== 'running';
      this.action.textContent = state === 'running' ? this.text.jump : state === 'paused' ? this.text.resume : state === 'finished' ? this.text.replay : this.text.start;
      this.title.textContent = state === 'paused' ? this.text.paused : state === 'finished' ? this.text.finished : this.text.ready;
      this.description.textContent = state === 'paused' ? (this.pauseMessage || this.text.pauseHelp) : state === 'finished' ? this.resultText() : this.text.intro;
      this.syncSound(); this.render();
    }
    syncSound() {
      this.soundButton.textContent = this.sound.muted ? this.text.soundOff : this.text.soundOn;
      this.soundButton.setAttribute('aria-pressed', String(this.sound.muted));
    }
    resultText() { return this.text.result.replace('[score]', this.model.score).replace('[coins]', this.model.coins); }
    updateHud() {
      this.score.textContent = String(this.model.coins).padStart(3, '0');
      this.shieldNode.hidden = !this.model.shield;
      this.slowNode.hidden = this.model.slow <= 0;
      this.slowNode.textContent = this.text.slow.replace('[seconds]', Math.ceil(this.model.slow));
      const events = this.model.events.splice(0);
      if (events.length) this.announce(this.text[events[events.length - 1] + 'Help']);
    }
    run() {
      if (this.frame || this.model.state !== 'running') return;
      this.frame = requestAnimationFrame(time => {
        this.frame = 0;
        if (document.hidden || !this.visible || !this.details.open) { this.pause(this.text.away); return; }
        if (this.last !== null) {
          const dt = (time - this.last) / 1000;
          if (dt > 0.25) { this.pause(this.text.away); return; }
          this.model.tick(dt);
        }
        this.last = time;
        if (this.model.state === 'finished') this.finish();
        else { this.sound.music(); this.updateHud(); this.render(); this.run(); }
      });
    }
    stopFrame() { cancelAnimationFrame(this.frame); this.frame = 0; this.last = null; }
    pause(message) {
      this.sound.silence();
      if (this.crashing) { this.completeCrash(); return; }
      if (this.model.state !== 'running') return;
      this.model.pause(); this.stopFrame(); this.pauseMessage = message || this.text.pauseHelp;
      this.sync(); this.announce(this.text.paused);
    }
    finish() {
      if (this.root.dataset.ppsState === 'finished') return;
      this.stopFrame(); this.finishAt = performance.now(); this.best = Math.max(this.best, this.model.coins);
      this.crashAge = 0; this.crashing = !this.reduced; this.sound.stopMusic(); this.sound.play();
      if (this.reduced) this.crashAge = CRASH_DURATION;
      this.sync(); this.announce(this.resultText());
      if (this.crashing) this.animateCrash();
    }
    animateCrash() {
      if (!this.crashing || this.frame) return;
      this.frame = requestAnimationFrame(time => {
        this.frame = 0;
        if (document.hidden || !this.visible || !this.details.open) { this.completeCrash(); return; }
        if (this.last !== null) this.crashAge += Math.min(0.05, Math.max(0, (time - this.last) / 1000));
        this.last = time;
        if (this.crashAge >= CRASH_DURATION) this.completeCrash();
        else { this.render(); this.animateCrash(); }
      });
    }
    completeCrash() { this.crashing = false; this.crashAge = CRASH_DURATION; this.stopFrame(); this.sync(); }
    close() {
      this.stopFrame(); this.sound.close(); this.crashing = false; this.crashAge = CRASH_DURATION; this.model.reset(); this.held.clear(); this.sync(); this.details.open = false;
      this.details.querySelector('summary').focus({ preventScroll: true }); this.announce(this.text.exited);
    }
    destroy() {
      this.destroyed = true; this.stopFrame(); this.sound.close(); this.crashing = false; this.abort.abort(); this.observer.disconnect(); this.resizeObserver.disconnect();
    }
    render() {
      const ctx = this.ctx; if (!ctx || !this.canvas.width) return;
      const m = this.model; const w = m.width; const ground = 260;
      const ink = '#171a16', cream = '#f3eddf', ochre = '#c4934d', red = '#d33b26';
      const scale = this.canvas.height / 330;
      ctx.setTransform(scale, 0, 0, scale, 0, 0);
      ctx.clearRect(0, 0, w, 330);
      ctx.fillStyle = cream; ctx.fillRect(0, 0, w, 330);
      ctx.fillStyle = '#e5dac3'; ctx.beginPath(); ctx.arc(w - 72, 84, 49, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#d7c9ad';
      for (let x = -20; x < w + 100; x += 118) { ctx.fillRect(x, 162, 92, 37); ctx.beginPath(); ctx.moveTo(x - 7, 162); ctx.lineTo(x + 46, 140); ctx.lineTo(x + 99, 162); ctx.closePath(); ctx.fill(); }
      ctx.fillStyle = '#ac9d83'; ctx.fillRect(0, 199, w, 3);
      ctx.fillStyle = '#e5dbc8'; ctx.fillRect(0, 202, w, 28);
      ctx.strokeStyle = '#c5b697'; ctx.lineWidth = 1;
      for (let x = 0; x < w; x += 36) { ctx.beginPath(); ctx.moveTo(x, 202); ctx.lineTo(x, 230); ctx.stroke(); }
      ctx.fillStyle = ink; ctx.fillRect(0, 230, w, 100);
      ctx.fillStyle = ochre; ctx.fillRect(0, 232, w, 3);
      const scroll = this.reduced ? 0 : m.distance;
      for (let x = -(scroll % 80); x < w; x += 80) {
        ctx.fillStyle = '#c44530'; ctx.fillRect(x, 246, 40, 10);
        ctx.fillStyle = '#eee6d4'; ctx.fillRect(x + 40, 246, 40, 10);
      }
      ctx.fillStyle = '#797b6c';
      for (let x = -(scroll % 150); x < w; x += 150) ctx.fillRect(x, 299, 60, 3);
      ctx.fillStyle = ochre; ctx.fillRect(0, 326, w, 4);
      for (const pickup of m.pickups) {
        const x = pickup.x, y = ground - pickup.height;
        ctx.save(); ctx.translate(x, y); ctx.lineWidth = 2; ctx.strokeStyle = ink;
        if (pickup.type === 'coin') {
          ctx.fillStyle = ochre; ctx.beginPath(); ctx.arc(0, 0, 10, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
          ctx.strokeStyle = cream; ctx.beginPath(); ctx.arc(0, 0, 6, 0, Math.PI * 2); ctx.stroke();
          ctx.fillStyle = ink; ctx.fillRect(-1, -4, 2, 8);
        } else {
          ctx.fillStyle = cream; ctx.beginPath(); ctx.arc(0, 0, 16, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
          ctx.fillStyle = '#426b78'; ctx.beginPath(); ctx.arc(0, 0, 10, 0, Math.PI * 2); ctx.fill();
          ctx.strokeStyle = cream; ctx.beginPath(); ctx.moveTo(0, -6); ctx.lineTo(0, 0); ctx.lineTo(5, 2); ctx.stroke();
        }
        ctx.restore();
      }
      for (const obstacle of m.obstacles) this.drawHazard(ctx, obstacle, ground, m.distance);
      this.drawKart(ctx, m, ground);
    }
    drawHazard(ctx, obstacle, ground, distance) {
      const ink = '#171a16', cream = '#f3eddf', ochre = '#c4934d', red = '#d33b26';
      const { x, width, height, type } = obstacle;
      ctx.save(); ctx.translate(x, ground); ctx.lineJoin = 'round'; ctx.lineWidth = 2; ctx.strokeStyle = ink;
      ctx.fillStyle = '#00000040'; ctx.beginPath(); ctx.ellipse(width / 2, 4, width / 2 + 5, 4, 0, 0, Math.PI * 2); ctx.fill();
      if (type === 'tires') {
        for (let i = 0; i < 3; i++) {
          const y = -height + i * 20;
          ctx.fillStyle = '#343a31'; ctx.beginPath(); ctx.roundRect(1, y + 3, width - 2, 20, 5); ctx.fill(); ctx.stroke();
          ctx.fillStyle = '#757c65'; ctx.beginPath(); ctx.ellipse(width / 2, y + 5, width / 2 - 2, 5, 0, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
          ctx.fillStyle = ink; ctx.beginPath(); ctx.ellipse(width / 2, y + 5, 9, 2, 0, 0, Math.PI * 2); ctx.fill();
          ctx.strokeStyle = '#626b54'; ctx.beginPath(); ctx.moveTo(10, y + 11); ctx.lineTo(8, y + 18); ctx.moveTo(30, y + 11); ctx.lineTo(32, y + 18); ctx.stroke(); ctx.strokeStyle = ink;
        }
        ctx.fillStyle = ochre; ctx.fillRect(17, -height + 10, 7, height - 11);
      } else if (type === 'barrier') {
        ctx.fillStyle = ink; ctx.fillRect(8, -height, 5, height); ctx.fillRect(width - 13, -height, 5, height);
        ctx.fillStyle = ochre; ctx.fillRect(-3, -8, 25, 8); ctx.fillRect(width - 22, -8, 25, 8); ctx.strokeRect(-3, -8, 25, 8); ctx.strokeRect(width - 22, -8, 25, 8);
        ctx.fillStyle = cream; ctx.fillRect(0, -height, width, 28);
        ctx.save(); ctx.beginPath(); ctx.rect(0, -height, width, 28); ctx.clip();
        ctx.fillStyle = red;
        for (let i = -30; i < width + 30; i += 28) { ctx.beginPath(); ctx.moveTo(i, -height); ctx.lineTo(i + 14, -height); ctx.lineTo(i + 40, -height + 28); ctx.lineTo(i + 26, -height + 28); ctx.closePath(); ctx.fill(); }
        ctx.restore(); ctx.strokeRect(0, -height, width, 28);
      } else if (type === 'roller') {
        ctx.translate(width / 2, -height / 2);
        ctx.fillStyle = ink; ctx.beginPath(); ctx.arc(0, 0, 22, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = '#858b76'; ctx.beginPath(); ctx.arc(0, 0, 18, 0, Math.PI * 2); ctx.stroke();
        if (!this.reduced) ctx.rotate(-(distance - (obstacle.offset || 0)) / 22);
        ctx.strokeStyle = ochre; ctx.lineWidth = 5; ctx.beginPath(); ctx.arc(0, 0, 17, -.35, .35); ctx.stroke();
        ctx.strokeStyle = cream; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(0, 0, 9, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(-9, 0); ctx.lineTo(9, 0); ctx.moveTo(0, -9); ctx.lineTo(0, 9); ctx.stroke();
      } else {
        ctx.fillStyle = red; ctx.beginPath(); ctx.moveTo(2, -3); ctx.lineTo(12, -35); ctx.lineTo(18, -35); ctx.lineTo(28, -3); ctx.closePath(); ctx.fill(); ctx.stroke();
        ctx.fillStyle = cream; ctx.beginPath(); ctx.moveTo(8, -23); ctx.lineTo(22, -23); ctx.lineTo(24, -16); ctx.lineTo(6, -16); ctx.closePath(); ctx.fill();
        ctx.fillStyle = ochre; ctx.fillRect(-3, -5, 36, 5); ctx.strokeRect(-3, -5, 36, 5);
      }
      ctx.restore();
    }
    drawKart(ctx, m, ground) {
      const ink = '#171a16', cream = '#f3eddf', ochre = '#c4934d', red = '#d33b26';
      const px = m.playerX, crashed = m.state === 'finished';
      const crash = crashed ? Math.min(1, this.crashAge / CRASH_DURATION) : 0;
      const bounce = crashed && !this.reduced ? Math.sin(crash * Math.PI) : 0;
      const landing = this.reduced ? 0 : Math.sin(Math.PI * m.landing / 0.24);
      const airborne = this.reduced ? 0 : Math.min(1, m.y / 50);
      ctx.fillStyle = '#00000055'; ctx.beginPath(); ctx.ellipse(px, ground + 8, 43 - m.y * 0.1, 5, 0, 0, Math.PI * 2); ctx.fill();
      if (m.shield) {
        ctx.strokeStyle = '#506d57'; ctx.lineWidth = 2; ctx.setLineDash([5, 5]);
        ctx.beginPath(); ctx.ellipse(px, ground - m.y - 42, 76, 66, 0, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]);
      }
      if (crashed && (crash < 1 || this.reduced)) {
        ctx.save(); ctx.translate(px + 55, ground - m.y - 25);
        ctx.globalAlpha = this.reduced ? 0.75 : 1 - crash;
        ctx.strokeStyle = ochre; ctx.lineWidth = 3;
        for (let i = 0; i < 7; i++) {
          const angle = i * Math.PI * 2 / 7, a = 10 + crash * 13, b = a + 8;
          ctx.beginPath(); ctx.moveTo(Math.cos(angle) * a, Math.sin(angle) * a);
          ctx.lineTo(Math.cos(angle) * b, Math.sin(angle) * b); ctx.stroke();
        }
        ctx.restore();
      }
      ctx.save(); ctx.translate(px - crash * 12, ground - m.y + landing * 3 - bounce * 18);
      if (!this.reduced) {
        if (crashed) ctx.rotate(-bounce * 0.2 + crash * 0.1);
        else if (m.y > 0) ctx.rotate(Math.max(-0.07, Math.min(0.06, -m.vy * 0.00015)));
      }
      ctx.strokeStyle = ink; ctx.lineJoin = 'round'; ctx.lineCap = 'round'; ctx.lineWidth = 2;
      ctx.fillStyle = '#3c4137'; ctx.beginPath(); ctx.moveTo(-46, -62); ctx.lineTo(-36, -62); ctx.lineTo(-20, -18); ctx.lineTo(-36, -18); ctx.closePath(); ctx.fill(); ctx.stroke();
      // Wheel behind the authentic right glove; bodywork occludes the legs.
      ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(23, -16); ctx.lineTo(31, -34); ctx.stroke();
      ctx.save(); ctx.translate(29, -32); ctx.rotate(-0.55);
      ctx.beginPath(); ctx.ellipse(0, 0, 10, 5, 0, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(-8, 0); ctx.lineTo(8, 0); ctx.stroke(); ctx.restore();
      ctx.save(); ctx.beginPath(); ctx.rect(-85, -110, 150, 90); ctx.clip();
      // Whole 384×384 original, uniformly scaled. No reshaped crop or invented limbs.
      if (this.pex.complete && this.pex.naturalWidth) ctx.drawImage(this.pex, -81, -107, 140, 140);
      else { ctx.fillStyle = red; ctx.beginPath(); ctx.moveTo(-10, -98); ctx.lineTo(24, -58); ctx.lineTo(-10, -19); ctx.lineTo(-46, -58); ctx.closePath(); ctx.fill(); }
      ctx.restore();
      ctx.strokeStyle = ink; ctx.lineWidth = 2; ctx.fillStyle = red;
      ctx.beginPath(); ctx.moveTo(-53, -21); ctx.lineTo(12, -21); ctx.lineTo(42, -26); ctx.lineTo(60, -20); ctx.lineTo(64, -11); ctx.lineTo(-55, -11); ctx.closePath(); ctx.fill(); ctx.stroke();
      ctx.fillStyle = cream; ctx.fillRect(-26, -20, 33, 10);
      ctx.fillStyle = ink; ctx.font = 'bold 8px Arial, sans-serif'; ctx.textAlign = 'center'; ctx.fillText('PEX', -9, -12);
      ctx.fillStyle = ochre; ctx.fillRect(48, -18, 20, 6); ctx.strokeRect(48, -18, 20, 6);
      ctx.strokeStyle = '#a7a68c'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(-52, -8); ctx.lineTo(54, -8); ctx.stroke();
      for (const x of [-37, 42]) {
        ctx.save(); ctx.translate(x, -10 - landing * 2 + airborne * 1.5);
        ctx.fillStyle = ink; ctx.beginPath(); ctx.arc(0, 0, 14, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = cream; ctx.beginPath(); ctx.arc(0, 0, 7, 0, Math.PI * 2); ctx.fill();
        if (!this.reduced) ctx.rotate(m.distance / 14);
        ctx.strokeStyle = ink; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(-6, 0); ctx.lineTo(6, 0); ctx.moveTo(0, -6); ctx.lineTo(0, 6); ctx.stroke(); ctx.restore();
      }
      ctx.restore();
    }
  }
  if (typeof module !== 'undefined' && module.exports) { module.exports = { PexRaceModel, PexRace, PexCrashSound, PHYSICS, PROGRESSION_DISTANCE, CRASH_DURATION, HAZARDS }; return; }
  const instances = new WeakMap();
  const mount = scope => {
    const roots = [...scope.querySelectorAll('[data-pex-pit-stop]')];
    if (scope.matches?.('[data-pex-pit-stop]')) roots.push(scope);
    roots.forEach(root => { if (!instances.has(root)) instances.set(root, new PexRace(root)); });
  };
  if (window.__diPexRaceMounted) return;
  window.__diPexRaceMounted = true;
  mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const roots = [...event.target.querySelectorAll('[data-pex-pit-stop]')];
    if (event.target.matches?.('[data-pex-pit-stop]')) roots.push(event.target);
    roots.forEach(root => { instances.get(root)?.destroy(); instances.delete(root); });
  });
})();
